<template>
	<CurvesPage class="archimed" @onScale="drawLine">
		
		<template #curveName>Спираль Архимеда</template>
		
		<template #curveFeature>
			
			<h5 class="mt-0">
				Уравнение:
				<b-badge variant="light" class="archimed__main-formula">
					<vue-mathjax :formula="'$$r=a + b \\theta $$'" class="formula">
					
					</vue-mathjax>
				</b-badge>
			</h5>
			
			<p>
				где <vue-mathjax :formula="'$$a$$'" class="formula"></vue-mathjax> и
				<vue-mathjax :formula="'$$b$$'" class="formula"></vue-mathjax> — параметры, определяющие начальный радиус
				спирали и расстояние между витками, которое равно
				<vue-mathjax :formula="'$$2\\pi b$$'" class="formula"></vue-mathjax>.
				Обратите внимание, что спираль Архимеда также иногда называют арифметической спиралью.
				Это имя связывают с арифметической зависимостью расстояния от начала кривой до точек спирали,
				находящихся на одной радиальной линии.
			</p>
			
			<h5 class="mt-0">Уравнение для декартовых координат:</h5>
			
			<p>
				<vue-mathjax :formula="'$$\\begin{cases}x=(a+b \\theta)\\cos \\theta \\\\ y=(a+b \\theta)\\sin \\theta \\end{cases}$$'" class="formula"></vue-mathjax>
			</p>
			<h5 class="mt-0">Область значений <vue-mathjax :formula="'$$ \\theta $$'" class="formula"></vue-mathjax>:</h5>
			<b-form-input v-model="maxTheta" type="range" min="0" max="100" @input="drawLine"></b-form-input>
			<p><vue-mathjax :formula="range" class="formula"></vue-mathjax></p>
			<h5>Зависимость от коэффициентов:</h5>
			<label>
				<vue-mathjax :formula="'$$a$$'" class="formula"></vue-mathjax>:
			</label>
			<b-form-spinbutton v-model="a" min="0" max="100" @input="drawLine" vertical class="archimed__koef">
			
			</b-form-spinbutton>
			<label>
				<vue-mathjax :formula="'$$b$$'" class="formula"></vue-mathjax>:
			</label>
			<b-form-spinbutton v-model="b" min="0" max="100" vertical @input="drawLine" class="archimed__koef" :disabled="tpb !== 0">
			
			</b-form-spinbutton>
			
			<label>
				<vue-mathjax :formula="'$$2 \\pi b$$'" class="formula"></vue-mathjax>:
			</label>
			<b-form-spinbutton v-model="tpb" min="0" max="100" vertical @input="drawLine" class="archimed__koef" :disabled="b !== 0">
			
			</b-form-spinbutton>
		</template>
		
		<template #curvePhoto>
			<img src="@/assets/img/archimed2.png" width="100" alt="Архимед">
		</template>
		
		<template #curveInfo>
			<h5 class="mt-0">Описание</h5>
			<p>
				Спираль Архимеда - плоская кривая, которую описывает точка, движущаяся равномерно-поступательно от центра 0 по равномерно-вращающемуся радиусу.
			</p>
			<h5 class="mt-0">Применение</h5>
			<p>
				В III веке до нашей эры Архимед на основе своей спирали изобрёл винт, который успешно применяли для передачи воды в оросительные каналы из водоёмов, расположенных ниже. Позже на основе винта Архимеда создали шнек («улитку»). Его очень известная разновидность – винтовой ротор в мясорубке. Шнек используют в механизмах для перемешивания материалов различной консистенции. В технике нашли применение антенны в виде спирали Архимеда. Самоцентрирующийся патрон выполнен по спирали Архимеда. Звуковые дорожки на CD и DVD дисках также имеют форму спирали Архимеда.
				<br><br>
				Спираль Архимеда нашла практическое применение в математике, технике, архитектуре, машиностроении.
			</p>
		</template>
		
		<template #curveTasks>
			<h5 class="mb-4">Примеры задач:</h5>
			<b-media>
				<template #aside>
					<h6>1)</h6>
				</template>
				<h6 class="mt-0">Найти длину первого витка спирали Архимеда при <vue-mathjax :formula="'$$ a = 0, b = 1 $$'" class="formula"></vue-mathjax></h6>
				<b-button v-b-toggle.answer-1 variant="dark">Показать решение</b-button>
				<b-collapse id="answer-1" class="mt-2 answer">
					<p>1) При данных значениях уравнение принимает вид:
					<vue-mathjax
							:formula="'$$ r=\\theta $$'"
							class="formula"></vue-mathjax>, где <vue-mathjax :formula="'$$ 0< \\theta <2 \\pi $$'" class="formula"></vue-mathjax>;
							<b-button variant="light" @click="taskDraw(0, 1, 2)" class="task__draw">Построить</b-button><br>
						2) Длина дуги равна: <vue-mathjax :formula="'$$ L=\\int_{0}^{2\\pi} \\sqrt{1+\\theta^2}d \\theta $$'" class="formula"></vue-mathjax>;<br>
						3) Найдём значение интеграла "по частям":</p>
					<vue-mathjax :formula="'$$ L=\\int_{0}^{2\\pi} \\sqrt{1+\\theta^2}d \\theta $$'" class="formula"></vue-mathjax> =
					<vue-mathjax
							:formula="'$$ \\begin{bmatrix} u=\\sqrt{1+\\theta^{2}} & du=\\frac{\\theta}{\\sqrt{1+\\theta^2}}d\\theta \\\\\\\\\n'+
'v= \\theta & dv=d \\theta \\end{bmatrix} $$'"
							class="formula"
					></vue-mathjax> =
					<vue-mathjax
							:formula="'$$ \\theta \\sqrt{1+\\theta^2}\\mid^{2\\pi}_0- \\int_{0}^{2\\pi}\\frac{\\theta^2}{\\sqrt{1+\\theta^2}} d \\theta $$'"
							class="formula"
					></vue-mathjax> =
					<vue-mathjax
							:formula="'$$ \\theta \\sqrt{1+\\theta^2}\\mid^{2\\pi}_0- \\int_{0}^{2\\pi}\\frac{(\\theta^2 + 1) - 1}{\\sqrt{1+\\theta^2}} d \\theta $$'"
							class="formula"
					></vue-mathjax> =
					<vue-mathjax
							:formula="'$$ 2\\pi\\sqrt{1+4\\pi^2}-\\int_{0}^{2\\pi}\\sqrt{1+\\theta^2}d\\theta + \\int_{0}^{2\\pi}\\frac{d\\theta}{\\sqrt{1+\\theta^2}} $$'"
							class="formula"
					></vue-mathjax> =
					<vue-mathjax
							:formula="'$$ 2\\pi\\sqrt{1+4\\pi^2}-L+ln(\\theta+\\sqrt{1+\\theta^2}d\\theta)\\mid^{2\\pi}_0 $$'"
							class="formula"
					></vue-mathjax> =
					<vue-mathjax
							:formula="'$$ 2\\pi\\sqrt{1+4\\pi^2}-L+ln(\\theta+\\sqrt{1+\\theta^2}d\\theta) $$'"
							class="formula"
					></vue-mathjax><br>
					4) Перенеся <vue-mathjax
						:formula="'$$ L $$'"
						class="formula"
				></vue-mathjax> в левую часть и разделив всё на 2, получим ответ:
					<vue-mathjax
							:formula="'$$ L=\\pi\\sqrt{1+4\\pi^2}+\\frac{1}{2}ln(2\\pi+\\sqrt{1+4\\pi^2}) $$'"
							class="formula"
					></vue-mathjax>
					
				</b-collapse>
			</b-media>
		</template>
		
	</CurvesPage>
</template>

<script>
	import CurvesPage from '@/views/templates/CurvesPage';
	
	export default {
		name: 'Archimedes',
		data () {
			return {
				curve: undefined,
				a: 0,
				b: 0,
				tpb: 0,
				maxTheta: 20
			}
		},
		computed: {
			range: function () {
				return '$$0 < \\theta <' + this.maxTheta.toString() + ' \\pi$$';
			}
		},
		methods: {
			taskDraw (a, b, t){
				this.a = a;
				this.b = b;
				this.maxTheta = t;
				this.drawLine();
			},
			drawLine () {
				
				this.$store.dispatch('scale', { curve:this.curve });
				// рисуем линию
				let a = this.a;
				let b = (this.b + this.tpb / (2 * Math.PI));
				this.curve.beginPath();
				let x = ((a) * Math.cos(0));
				let y = ((a) * Math.sin(0));
				this.curve.moveTo(160 + x * this.$store.state.step, 160 - y * this.$store.state.step);
				
				for (let i = 0; i < this.maxTheta * Math.PI; i+=0.05) {
					x = (a + b * i) * Math.cos(i);
					y = (a + b * i) * Math.sin(i);
					this.curve.lineTo(160 + x  * this.$store.state.step, 160 - y * this.$store.state.step);
				}
				this.curve.stroke();
			}
		},
		mounted () {
			this.curve = document.getElementById('curve').getContext('2d');
			this.$store.dispatch('scale', { step:this.$store.state.step, curve:this.curve });
		},
		components: {
			CurvesPage
		}
	}
</script>

<style scoped lang="scss">
	.archimed {
		.archimed__main-formula {
			margin-left: 30px;
		}
		
		.archimed__koef {
			margin-left: 30px;
			margin-right: 30px;
			margin-top: -10px;
		}
	}
</style>
